import {
  Bullet,
  Bullets,
  Callout,
  Internal,
  LegalDocument,
  Mail,
  P,
  SubHeading,
  type LegalSection,
} from '../../components/legal';
import { COMPANY, PAYMENTS, SELLER_NOTICE } from '../../lib/company';
import { usePageTitle } from '../../lib/usePageTitle';

/**
 * Refund Policy.
 *
 * We sell directly, so we are the party that takes the payment and returns it
 * — there is no reseller to route a buyer to. That makes this document's job
 * simpler than it was, and one thing more important: a buyer who cannot work
 * out who to ask files a chargeback instead, which costs everyone more than
 * the refund. So the answer to "who do I ask" appears in the intro, in its own
 * section, and again under chargebacks.
 *
 * The windows below (14 days on a first payment, 7 days on an unused renewal)
 * are a commercially normal starting point for SaaS, chosen so the policy
 * comfortably exceeds the EU/UK statutory 14 days rather than fighting it.
 * They are a proposal for the founders to confirm, not a decision — spec 76
 * still lists subscription billing rules as open.
 */
const UPDATED = '2026-08-17';

const SECTIONS: LegalSection[] = [
  {
    id: 'summary',
    title: 'The short version',
    body: (
      <>
        <Callout>
          Not happy in the first 14 days of a new paid subscription? Email{' '}
          <Mail address={COMPANY.email.support} /> and we will refund it in full. Charged for
          a renewal you meant to cancel and have not used? Tell us within 7 days and we will
          refund that too.
        </Callout>
        <P>
          The rest of this page sets out the detail, including the cases where a refund is
          not available and how the money actually gets back to you.
        </P>
      </>
    ),
  },
  {
    id: 'who-refunds',
    title: 'Who issues the refund',
    body: (
      <>
        <P>{SELLER_NOTICE}</P>
        <P>
          We take the payment and we return it. There is no reseller in between, so there is
          one route and one address:
        </P>
        <Bullets>
          <Bullet>
            <strong className="text-white">Email</strong>{' '}
            <Mail address={COMPANY.email.support} />, quoting the order reference from your
            receipt. If you cannot find the receipt, the email address on the account is
            enough for us to find the order.
          </Bullet>
        </Bullets>
        <P>
          The refund is returned to the card that paid. Once we have issued it, how long it
          takes to appear is set by your bank rather than by us &mdash; usually a few working
          days, occasionally longer over a statement boundary.
        </P>
        <P>
          If a charge on your statement looks unfamiliar, it may carry the name of{' '}
          {PAYMENTS.gatewayName ?? 'our payment processor'} rather than {COMPANY.product}.
          Send us the date and amount and we will identify it.
        </P>
      </>
    ),
  },
  {
    id: 'subscriptions',
    title: 'Subscriptions',
    body: (
      <>
        <SubHeading>Your first payment on a paid plan</SubHeading>
        <P>
          If the service is not right for you, ask within{' '}
          <strong className="text-white">14 days</strong> of your first payment on a paid plan
          and we will refund it in full. You do not have to give a reason, and we do not
          reduce the refund for hours you used while evaluating — that is what an evaluation
          is for.
        </P>

        <SubHeading>Renewals</SubHeading>
        <P>
          Subscriptions renew automatically, and we email you before each renewal. If a
          renewal is charged and you meant to cancel, contact us within{' '}
          <strong className="text-white">7 days</strong> of the charge. Provided you have not
          made significant use of the new period, we will refund it and cancel the
          subscription.
        </P>

        <SubHeading>Cancelling mid-period</SubHeading>
        <P>
          You can cancel at any time from your subscription dashboard. Cancelling stops the
          next renewal and keeps your plan active until the end of the period you have
          already paid for. Outside the windows above we do not refund the unused part of a
          period, because the capacity for that period has already been reserved for you.
        </P>

        <SubHeading>Downgrades and upgrades</SubHeading>
        <P>
          An upgrade takes effect when the payment is confirmed. A downgrade takes effect at
          the next renewal, so you keep the entitlement you paid for until then; a downgrade
          is not a refund of the difference.
        </P>
      </>
    ),
  },
  {
    id: 'free-plan',
    title: 'The Free plan',
    body: (
      <P>
        Nothing is charged for the Free plan, so there is nothing to refund. It exists so you
        can run a real verification against a real project before paying anything — please do
        that first. See the <Internal to="/pricing">Pricing page</Internal> for what it
        includes.
      </P>
    ),
  },
  {
    id: 'professional-services',
    title: 'Professional setup and support',
    body: (
      <>
        <P>
          The one-time setup engagement and the monthly support retainer are services rather
          than software, so they work differently:
        </P>
        <Bullets>
          <Bullet>
            <strong className="text-white">Before work starts</strong> — cancel the engagement
            for a full refund of any deposit paid.
          </Bullet>
          <Bullet>
            <strong className="text-white">Once work has started</strong> — the deposit covers
            work already done and is not refundable, but you are not billed for milestones
            not yet reached. If we fail to deliver an agreed milestone, we refund the amount
            attributable to it.
          </Bullet>
          <Bullet>
            <strong className="text-white">Monthly support</strong> — cancel at any time to
            stop future months. The current month is not refunded once support hours have
            been drawn on.
          </Bullet>
        </Bullets>
        <P>
          Where a written statement of work sets out different terms, that document prevails
          over this section.
        </P>
      </>
    ),
  },
  {
    id: 'when-we-refund-anyway',
    title: 'When we refund outside these windows',
    body: (
      <>
        <P>The windows above are a floor, not a rule to hide behind. We will also refund if:</P>
        <Bullets>
          <Bullet>
            the service was materially unavailable or broken for a sustained part of your
            billing period and we could not fix it;
          </Bullet>
          <Bullet>
            you were charged in error, charged twice, or charged after a cancellation we
            failed to process;
          </Bullet>
          <Bullet>
            someone paid on your account without authorisation — tell us immediately at{' '}
            <Mail address={COMPANY.email.security} /> so we can secure the account as well as
            refund the charge.
          </Bullet>
        </Bullets>
      </>
    ),
  },
  {
    id: 'exclusions',
    title: 'What is not refundable',
    body: (
      <Bullets>
        <Bullet>
          Periods already consumed, outside the windows in this policy.
        </Bullet>
        <Bullet>
          Accounts terminated for breach of the{' '}
          <Internal to="/terms">Terms of Service</Internal> — for example directing the agent
          at systems you were not authorised to test.
        </Bullet>
        <Bullet>
          Dissatisfaction with a verdict the agent reached, where the service ran as
          described. {COMPANY.product} reports the evidence it found; it does not guarantee a
          particular outcome.
        </Bullet>
        <Bullet>
          Charges older than 120 days, which we may be unable to reverse through the original
          payment method.
        </Bullet>
      </Bullets>
    ),
  },
  {
    id: 'how-to-request',
    title: 'How to request a refund',
    body: (
      <>
        <P>
          Email <Mail address={COMPANY.email.support} /> with:
        </P>
        <Bullets>
          <Bullet>the email address on the account;</Bullet>
          <Bullet>
            the order or invoice reference from your receipt, or the date and amount of
            the charge;
          </Bullet>
          <Bullet>a sentence on what went wrong, if something did.</Bullet>
        </Bullets>
        <P>
          We acknowledge refund requests within 2 business days and aim to decide within 5.
          Once approved, we return the money to the original payment method; how quickly it
          appears is up to your bank, and typically takes 3 to 10 business days.
        </P>
      </>
    ),
  },
  {
    id: 'statutory-rights',
    title: 'Your statutory rights',
    body: (
      <P>
        If you are a consumer in the UK, EU or another region with an equivalent regime, you
        may have a statutory right to withdraw from a distance contract within 14 days.
        Nothing in this policy limits that or any other right you have by law. Where a
        statutory right gives you more than this policy does, the statutory right applies.
      </P>
    ),
  },
  {
    id: 'chargebacks',
    title: 'Chargebacks',
    body: (
      <P>
        Please come to us before raising a chargeback with your bank. A chargeback takes
        weeks, freezes the account while it is investigated, and usually ends where a direct
        request would have ended in a day. If a charge looks unfamiliar, email{' '}
        <Mail address={COMPANY.email.support} /> with the date and amount and we will
        identify it for you.
      </P>
    ),
  },
];

export function Refunds() {
  usePageTitle('Refund Policy — WaveQA');

  return (
    <LegalDocument
      eyebrow="Legal"
      title="Refund Policy"
      intro={
        <>
          When you can get your money back, and how to ask. We sell directly, so refunds
          come from us &mdash; one address, no reseller in between.
        </>
      }
      updated={UPDATED}
      sections={SECTIONS}
    />
  );
}
